import { Input as InputPrimitive } from "@base-ui/react/input";
import { cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../utils/class-name";

const inputVariants = cva(
    "h-8.5 w-full min-w-0 rounded-[inherit] px-[calc(--spacing(3)-1px)] leading-8.5 outline-none [transition:background-color_5000000s_ease-in-out_0s] placeholder:text-muted-foreground/72 sm:h-7.5 sm:leading-7.5",
    {
        variants: {
            size: {
                sm: "h-7.5 px-[calc(--spacing(2.5)-1px)] leading-7.5 sm:h-6.5 sm:leading-6.5",
                default: "",
                lg: "h-9.5 leading-9.5 sm:h-8.5 sm:leading-8.5",
            },
        },
        defaultVariants: {
            size: "default",
        },
    },
);

export type InputProps = Omit<InputPrimitive.Props & React.RefAttributes<HTMLInputElement>, "size"> & {
    size?: "sm" | "default" | "lg" | number;
    unstyled?: boolean;
    nativeInput?: boolean;
    inputClassName?: string;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, size = "default", unstyled = false, nativeInput = false, style, inputClassName, ...props }, ref) => {
        const sizeVariant = typeof size === "string" ? size : undefined;

        const innerClassName = cn(
            inputVariants({ size: sizeVariant }),
            props.type === "search" &&
                "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
            props.type === "file" &&
                "text-muted-foreground file:me-3 file:bg-transparent file:font-medium file:text-foreground file:text-sm",
            inputClassName,
        );

        return (
            <span
                className={
                    cn(
                        !unstyled &&
                            "relative inline-flex w-full rounded-lg border border-input bg-background not-dark:bg-clip-padding text-base text-foreground shadow-xs/5 ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-ring has-autofill:bg-foreground/4 has-disabled:opacity-55 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[3px] sm:text-control dark:bg-input/32 dark:has-autofill:bg-foreground/8 dark:has-aria-invalid:ring-destructive/24 dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]",
                        className,
                    ) || undefined
                }
                data-size={size}
                data-slot={"input-control"}
            >
                {nativeInput ? (
                    <input
                        ref={ref}
                        className={innerClassName}
                        data-slot={"input"}
                        size={typeof size === "number" ? size : undefined}
                        style={style as React.CSSProperties | undefined}
                        {...props}
                    />
                ) : (
                    <InputPrimitive
                        ref={ref}
                        className={innerClassName}
                        data-slot={"input"}
                        size={typeof size === "number" ? size : undefined}
                        style={(state) => (typeof style === "function" ? style(state) : style)}
                        {...props}
                    />
                )}
            </span>
        );
    },
);
Input.displayName = "Input";

export { Input, InputPrimitive };
